/**
 * Table to spreadsheet, in the browser.
 *
 * Every export on the overview is a handful of rows the page is already
 * holding, so a server endpoint for them would be a round trip that reformats
 * data nobody has to fetch again. Anything big enough to need paging (the
 * invoice ledger, the whole client book) belongs on the API instead - this is
 * for the card-sized cuts.
 *
 * Two decisions that look like details and are not:
 *
 * - The separator is a semicolon and the file carries a UTF-8 BOM. Excel picks
 *   the field separator from the system's list separator, which on a Baltic or
 *   central-European Windows is a semicolon; a comma-separated file opens there
 *   as one column of text. The BOM is what stops that same Excel from mangling
 *   every accented letter in a company name.
 * - A TEXT field that starts with `=`, `+`, `-` or `@` is prefixed with a
 *   quote. Spreadsheets treat those as formulas, and a company that named
 *   itself "=SUM(A1)" would otherwise have its name executed on open. Numbers
 *   are exempt: a negative amount is the commonest cell in an accounting
 *   export, and neutralising it would turn every credit note into text that
 *   cannot be summed.
 */

/** A cell as it comes out of the app; anything else is stringified. */
export type CsvCell = string | number | null | undefined;

const SEP = ";";
const BOM = "﻿";

function cell(value: CsvCell): string {
  if (value === null || value === undefined) return "";
  const raw = String(value);
  const safe = typeof value === "string" && /^[=+\-@]/.test(raw) ? `'${raw}` : raw;
  return /["\n\r;,]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** The text of the file, header row included - callers pass it as the first
 *  row, because a header is just a row with different words in it. */
export function toCsv(rows: CsvCell[][]): string {
  return rows.map((row) => row.map(cell).join(SEP)).join("\r\n");
}

/** Hands the browser a file. Kept separate from `toCsv` so the formatting can
 *  be tested without a DOM. */
export function downloadCsv(filename: string, rows: CsvCell[][]): void {
  const blob = new Blob([BOM + toCsv(rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Freed on the next tick rather than immediately: Safari has not started the
  // download by the time click() returns, and revoking under it cancels it.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** `invoices-2026-08-07`. A date in the name is what stops the third export of
 *  the day from being called "invoices (2)". */
export function csvName(prefix: string, at: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${prefix}-${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}
